import { createLogic } from 'redux-logic';
import { stopSubmit, destroy } from 'redux-form';
import moment from 'moment';
import jwtDecode from 'jwt-decode';

import { types as authTypes, actions as authActions } from '../ducks/auth.js';
import { types as appTypes, actions as appActions } from '../ducks/app.js';
import {
  types as routingTypes,
  actions as routingActions,
} from '../ducks/routing.js';
import authApi from '../api/auth.js';
import 'url-search-params-polyfill';
import { getCurrentOutage, inOutage } from '../helpers/outageHelpers';
import { getErrorVar } from '../helpers/queryHelpers';
import Cookies from 'universal-cookie';
import axios from 'axios/lib/axios';

function generateBaseConfig(type, form) {
  return {
    type: type,
    debounce: 500,
    latest: true,
    processOptions: {
      dispatchReturn: true,
      successType: `${type}_SUCCESS`,
      failType: (err) => stopSubmit(form, err.response.data),
    },
  };
}

const getAuthToken = createLogic({
  type: authTypes.GET_AUTH_TOKEN,
  process({ getState, action }, dispatch, done) {
    const errorVar = getErrorVar();
    let data = [];
    if (errorVar) {
      dispatch({
        type: authTypes.GET_AUTH_TOKEN_FAILURE,
        payload: data,
      });
      done();
    } else {
      let data = [];
      axios
        .get(`/auth/signin`)
        .then((response) => {
          if (response.data && response.data.id_token) {
            let decodedJwt;
            try {
              decodedJwt = jwtDecode(response.data.id_token);
            } catch (err) {
              dispatch(authActions.signout());
              done();
            }
            data['idToken'] = response.data.id_token;
            data['decodedJwt'] = decodedJwt;

            if (portalForm) {
              for (const form of portalForm) {
                const key =
                  decodedJwt.info.customer_reference_number +
                  '.dismissed-survey' +
                  form.id;
                localStorage.removeItem(key);
              }
            }

            dispatch({
              type: authTypes.GET_AUTH_TOKEN_SUCCESS,
              payload: data,
            });
            dispatch(routingActions.goDash());
            done();
          } else {
            window.location.href = '/auth/';
          }
        })
        .catch((err) => {
          window.location.href = '/auth/';
        });
    }
  },
});

let signoutConfig = generateBaseConfig(authTypes.SIGNOUT);
signoutConfig.processOptions.successType = null;
const signout = createLogic({
  ...signoutConfig,
  process({ getState, action }, dispatch, done) {
    const { signout } = getState().auth;
    const { unsaved } = getState().app;
    const { type } = getState().location;
    const {
      payload: { force },
    } = action;

    // Attempt route change to show confirm leave dialog.
    // @todo Should be a better way to do this?
    if (
      type == routingTypes.PROFILE ||
      type == routingTypes.WORK_REQUESTS_CREATE ||
      type == routingTypes.ARRANGEMENT_CREATE
    ) {
      dispatch(routingActions.goProfileEdit());
    }

    // Only signout if user is not in an unsaved state and the Logout action was selected.
    if ((!unsaved || force) && signout) {
      dispatch(authActions.clearAuth());

      setTimeout(() => logout(), 10);
    } else {
      done();
    }
  },
});

const tick = createLogic({
  type: authTypes.TICK,
  process({ getState, action }, dispatch, done) {
    const {
      jwtExpiry,
      idleTicks,
      authTicks,
      authenticated,
      lastActive,
      prevLastActive,
    } = getState().auth;
    const currentOutage = getCurrentOutage(outages);
    if (currentOutage && inOutage(currentOutage)) {
      dispatch(appActions.setAppOutage(currentOutage));
    }

    if (authenticated) {
      const now = moment().unix();
      const authMins = authTicks / 60;
      const idleMins = idleTicks / 60;
      const timeoutMins = 10;
      const refreshMins = 0.5;

      dispatch(authActions.TICK);
      if (idleMins > timeoutMins) {
        // If user times out, force signout without confirmation dialog.
        dispatch(appActions.setUnsaved(false));
        dispatch(authActions.clearSignout());
        dispatch(authActions.signout(true));
      } else if (authMins > timeoutMins) {
        let endDate = moment.unix(jwtExpiry);
        let startDate = moment.unix(jwtExpiry).subtract(refreshMins, 'minutes');
        if (moment().isBetween(startDate, endDate)) {
          dispatch(authActions.refresh());
        }
      }
    }
    done();
  },
});

const refresh = createLogic({
  type: authTypes.REFRESH_REQUEST,
  process({ getState, action }, dispatch, done) {
    const { jwtExpiry, idleTicks, authTicks } = getState().auth;
    const { error } = action.payload;
    authApi
      .refresh()
      .then((resp) => resp.data)
      .then((data) => {
        let decodedJwt;
        try {
          decodedJwt = jwtDecode(data.id_token);
        } catch (err) {
          dispatch(authActions.signout());
          done();
        }
        data['decodedJwt'] = decodedJwt;
        data['idToken'] = data.id_token;

        dispatch({
          type: authTypes.REFRESH_SUCCESS,
          payload: data,
        });
      })
      .catch((err) => {
        dispatch({
          type: authTypes.REFRESH_FAILURE,
        });
      })
      .then(() => done());
  },
});

function logout() {
  window.location.href = '/auth/signout';
}

export default [getAuthToken, signout, tick, refresh];
